import React, { useEffect, useRef, useState } from 'react';
import QuickPriceRuleEquivalent from './QuickPriceRuleEquivalent';
import QuickPriceRuleBadge from './QuickPriceRuleBadge';
import { getQuickRuleEquivalent, getQuickRuleMargin, formatMarginRuleLabel } from './priceRuleEquivalence';
import {
  findComplexPriceRule,
  formatComplexRangeLabel,
  normalizePriceRuleSelection,
  resolvePriceRuleSelection,
} from '../../utils/complexPriceRules';

const AUTOSAVE_DELAY_MS = 600;
const RULE_MODES = ['markup_amount', 'markup_percent', 'fixed_price'];

const complexMode = (complexPriceRuleId) => `complex:${complexPriceRuleId}`;
const isComplexMode = (mode) => String(mode || '').startsWith('complex:');

const normalizeRule = (selection, { normalized = false } = {}) => {
  const normalizedSelection = normalizePriceRuleSelection(
    normalized ? selection : null,
    normalized ? null : selection
  );
  if (normalizedSelection?.type === 'complex') {
    return { mode: complexMode(normalizedSelection.complexPriceRuleId), value: '' };
  }
  const rule = normalizedSelection?.rule;
  return {
    mode: RULE_MODES.includes(rule?.mode) ? rule.mode : '',
    value: rule?.value === null || rule?.value === undefined ? '' : String(rule.value),
  };
};

const parseRule = (draft, { normalized = false, basis = 'unit' } = {}) => {
  if (!draft.mode) return { value: null, signature: '' };
  if (isComplexMode(draft.mode)) {
    const complexPriceRuleId = Number(String(draft.mode).slice('complex:'.length));
    if (!Number.isSafeInteger(complexPriceRuleId) || complexPriceRuleId <= 0) {
      return { error: 'Elige una regla compleja válida.' };
    }
    return {
      value: { type: 'complex', complexPriceRuleId },
      signature: `complex:${complexPriceRuleId}`,
    };
  }
  const parsed = Number(String(draft.value ?? '').trim());
  const requiresInteger = draft.mode !== 'markup_percent';
  if (!String(draft.value ?? '').trim()
    || !Number.isFinite(parsed)
    || parsed < 0
    || (requiresInteger && !Number.isInteger(parsed))) {
    return {
      error: requiresInteger
        ? 'Ingresa un valor entero válido.'
        : 'Ingresa un porcentaje válido.',
    };
  }
  const simpleRule = { mode: draft.mode, value: parsed };
  return {
    value: normalized
      ? { type: 'simple', rule: { ...simpleRule, basis: basis === 'kg' ? 'kg' : 'unit' } }
      : simpleRule,
    signature: `simple:${draft.mode}:${parsed}:${normalized ? basis : ''}`,
  };
};

const InlineQuickPriceRuleInput = ({
  formatId,
  value,
  priceRule,
  complexPriceRules = [],
  matchedRange = null,
  disabled = false,
  saving = false,
  onCommit,
  onDraftChange,
  onDirtyChange,
  modeAriaLabel = 'Regla de precio',
  valueAriaLabel = 'Valor de regla de precio',
  debounceMs = AUTOSAVE_DELAY_MS,
  resetSignal,
  referenceCost = null,
  basis = 'unit',
}) => {
  const usesNormalizedSelection = priceRule !== undefined;
  const serverSelection = usesNormalizedSelection ? priceRule : value;
  const parserOptions = { normalized: usesNormalizedSelection, basis };
  const initialDraft = normalizeRule(serverSelection, { normalized: usesNormalizedSelection });
  const [draft, setDraft] = useState(initialDraft);
  const [validationError, setValidationError] = useState('');
  const [internallySaving, setInternallySaving] = useState(false);
  const draftRef = useRef(initialDraft);
  const serverRuleRef = useRef(initialDraft);
  const lastSubmittedRef = useRef(null);
  const queuedRuleRef = useRef(null);
  const activeCommitRef = useRef(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const onCommitRef = useRef(onCommit);
  const onDirtyChangeRef = useRef(onDirtyChange);
  const commitRuleRef = useRef(null);
  const wasDisabledRef = useRef(disabled);
  onCommitRef.current = onCommit;
  onDirtyChangeRef.current = onDirtyChange;

  const updateDraft = (nextDraft) => {
    draftRef.current = nextDraft;
    setDraft(nextDraft);
  };

  const clearTimer = () => {
    if (!timerRef.current) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const reportDirty = (nextDraft) => {
    const parsed = parseRule(nextDraft, parserOptions);
    const server = parseRule(serverRuleRef.current, parserOptions);
    onDirtyChangeRef.current?.(Boolean(
      parsed.error || server.error || parsed.signature !== server.signature
    ));
  };

  const commitRule = async (nextDraft) => {
    clearTimer();
    const parsed = parseRule(nextDraft, parserOptions);
    if (parsed.error) {
      if (mountedRef.current) setValidationError(parsed.error);
      return false;
    }
    const server = parseRule(serverRuleRef.current, parserOptions);
    if (!server.error && parsed.signature === server.signature && !activeCommitRef.current) {
      if (mountedRef.current) setValidationError('');
      if (mountedRef.current) reportDirty(nextDraft);
      return true;
    }
    if (activeCommitRef.current) {
      queuedRuleRef.current = { draft: nextDraft, signature: parsed.signature };
      return true;
    }

    activeCommitRef.current = true;
    lastSubmittedRef.current = parsed.signature;
    if (mountedRef.current) {
      setValidationError('');
      setInternallySaving(true);
    }
    let succeeded = false;
    try {
      const result = await onCommitRef.current(parsed.value);
      succeeded = result !== false;
      if (succeeded) {
        serverRuleRef.current = normalizeRule(parsed.value, { normalized: usesNormalizedSelection });
        lastSubmittedRef.current = null;
        if (mountedRef.current) reportDirty(draftRef.current);
      }
      return result;
    } finally {
      if (!succeeded && lastSubmittedRef.current === parsed.signature) {
        lastSubmittedRef.current = null;
      }
      activeCommitRef.current = false;
      const queued = queuedRuleRef.current;
      queuedRuleRef.current = null;
      if (mountedRef.current) setInternallySaving(false);
      if (queued && (!succeeded || queued.signature !== parsed.signature)) {
        void commitRule(queued.draft);
      }
    }
  };
  commitRuleRef.current = commitRule;

  const scheduleCommit = (nextDraft) => {
    clearTimer();
    const parsed = parseRule(nextDraft, parserOptions);
    if (parsed.error) return;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void commitRule(draftRef.current);
    }, debounceMs);
  };

  const serverKey = JSON.stringify(initialDraft);
  useEffect(() => {
    const nextServerRule = JSON.parse(serverKey);
    const previousServer = serverRuleRef.current;
    serverRuleRef.current = nextServerRule;
    const current = draftRef.current;
    const currentParsed = parseRule(current, parserOptions);
    const previousParsed = parseRule(previousServer, parserOptions);
    const nextParsed = parseRule(nextServerRule, parserOptions);
    const incomingAcknowledgesSubmission = lastSubmittedRef.current !== null
      && !currentParsed.error
      && !nextParsed.error
      && currentParsed.signature === lastSubmittedRef.current
      && nextParsed.signature === lastSubmittedRef.current;
    const mayFollowServer = (!currentParsed.error
      && !previousParsed.error
      && currentParsed.signature === previousParsed.signature)
      || incomingAcknowledgesSubmission;
    if (mayFollowServer) {
      updateDraft(nextServerRule);
      setValidationError('');
      onDirtyChangeRef.current?.(false);
      if (incomingAcknowledgesSubmission) lastSubmittedRef.current = null;
    }
  }, [serverKey]);

  useEffect(() => {
    if (resetSignal === undefined) return;
    clearTimer();
    queuedRuleRef.current = null;
    lastSubmittedRef.current = null;
    updateDraft(serverRuleRef.current);
    setValidationError('');
    onDirtyChangeRef.current?.(false);
  }, [resetSignal]);

  useEffect(() => {
    const wasDisabled = wasDisabledRef.current;
    wasDisabledRef.current = disabled;
    if (disabled) {
      clearTimer();
      return;
    }
    if (!wasDisabled) return;
    const parsed = parseRule(draftRef.current, parserOptions);
    const server = parseRule(serverRuleRef.current, parserOptions);
    if (parsed.error
      || (!server.error && parsed.signature === server.signature)) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void commitRuleRef.current(draftRef.current);
    }, debounceMs);
  }, [debounceMs, disabled]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
      onDirtyChangeRef.current?.(false);
    };
  }, []);

  const patchDraft = (patch) => {
    const nextDraft = { ...draftRef.current, ...patch };
    updateDraft(nextDraft);
    setValidationError('');
    onDraftChange?.(nextDraft);
    reportDirty(nextDraft);
    scheduleCommit(nextDraft);
  };

  const busy = saving || internallySaving;
  const editorId = `quick-rule-${formatId}`;
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      clearTimer();
      const restoredRule = serverRuleRef.current;
      const restored = parseRule(restoredRule, parserOptions);
      queuedRuleRef.current = activeCommitRef.current && !restored.error
        ? { draft: restoredRule, signature: restored.signature }
        : null;
      updateDraft(restoredRule);
      setValidationError('');
      onDraftChange?.(restoredRule);
      reportDirty(restoredRule);
      return;
    }
    if (event.key !== 'Enter') return;
    event.preventDefault();
    void commitRule(draftRef.current);
  };

  const selectedComplexId = isComplexMode(draft.mode)
    ? Number(String(draft.mode).slice('complex:'.length))
    : null;
  const selectedComplexRule = findComplexPriceRule(complexPriceRules, selectedComplexId);
  const selectedResolution = resolvePriceRuleSelection(
    selectedComplexId
      ? { type: 'complex', complexPriceRuleId: selectedComplexId }
      : draft.mode ? { type: 'simple', rule: { ...draft, basis } } : null,
    { complexPriceRules, referenceCost, basis }
  );
  const savedComplexId = usesNormalizedSelection && priceRule?.type === 'complex'
    ? Number(priceRule.complexPriceRuleId)
    : null;
  const effectiveMatchedRange = selectedComplexId
    ? (selectedComplexId === savedComplexId ? matchedRange : null)
      || selectedResolution.matchedRange
    : null;
  const equivalent = getQuickRuleEquivalent(
    selectedResolution.resolvedRule || draft,
    referenceCost
  ) || (selectedComplexId ? { unavailable: 'unresolved_rule' } : null);
  const describedBy = [
    validationError ? `${editorId}-error` : null,
    selectedComplexId ? `${editorId}-complex-note` : null,
    equivalent ? `${editorId}-equivalent` : null,
  ].filter(Boolean).join(' ') || undefined;

  return (
    <div
      className="min-w-0"
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        void commitRule(draftRef.current);
      }}
    >
      <div className="mb-1 flex items-center gap-1">
        <QuickPriceRuleBadge>
          {formatMarginRuleLabel(selectedResolution.resolvedRule || (draft.mode ? draft : null), referenceCost, basis)}
        </QuickPriceRuleBadge>
      </div>
      <div className={`grid min-w-[12.5rem] items-start gap-1.5 ${
        selectedComplexId ? 'grid-cols-1' : 'grid-cols-[minmax(7rem,1fr)_5rem]'
      }`}>
        <select
          id={`${editorId}-mode`}
          aria-label={modeAriaLabel}
          aria-describedby={describedBy}
          aria-busy={busy}
          value={draft.mode}
          disabled={disabled}
          onChange={(event) => patchDraft({
            mode: event.target.value,
            value: '',
          })}
          className="min-w-0 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100"
        >
          <option value="">Sin regla</option>
          <option value="markup_amount">Sumar $</option>
          <option value="markup_percent">Sumar %</option>
          <option value="fixed_price">Precio fijo</option>
          {complexPriceRules.length > 0 && (
            <optgroup label="Reglas complejas">
              {complexPriceRules.map((rule) => (
                <option key={rule.id} value={complexMode(rule.id)}>{rule.name}</option>
              ))}
            </optgroup>
          )}
        </select>
        {!selectedComplexId && <div><div className="relative">
          <input
            id={`${editorId}-value`}
            aria-label={valueAriaLabel}
            aria-invalid={Boolean(validationError)}
            aria-describedby={describedBy}
            aria-busy={busy}
            type="number"
            inputMode={draft.mode === 'markup_percent' ? 'decimal' : 'numeric'}
            min="0"
            step={draft.mode === 'markup_percent' ? 'any' : '1'}
            value={draft.value}
            disabled={disabled || !draft.mode}
            placeholder={draft.mode === 'markup_percent' ? '%' : '$'}
            onChange={(event) => patchDraft({ value: event.target.value })}
            onKeyDown={handleKeyDown}
            className={`no-number-spinner w-full rounded-md border px-2 py-1.5 pr-6 text-xs font-semibold text-slate-900 outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-100 ${
              validationError
                ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                : 'border-slate-300 focus:border-blue-500 focus:ring-blue-100'
            }`}
          />
          {busy && (
            <span role="status" aria-label="Guardando regla" className="absolute inset-y-0 right-1.5 flex items-center">
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
            </span>
          )}
        </div>
          <QuickPriceRuleEquivalent id={`${editorId}-equivalent`} equivalent={equivalent}
            ruleMode={(selectedResolution.resolvedRule || draft).mode}
            margin={getQuickRuleMargin(selectedResolution.resolvedRule || draft, referenceCost)} basis={basis} />
        </div>}
      </div>
      {selectedComplexId && (
        <p id={`${editorId}-complex-note`} className="mt-1 text-[11px] leading-tight text-blue-700">
          {effectiveMatchedRange
            ? formatComplexRangeLabel(
              effectiveMatchedRange,
              Math.max(0, (selectedComplexRule?.ranges || []).findIndex(
                (range) => String(range.id) === String(effectiveMatchedRange.id)
              ))
            )
            : 'Sin rango efectivo hasta disponer de un costo válido.'}
          {busy && <span role="status" className="ml-1 font-semibold">Guardando…</span>}
        </p>
      )}
      {selectedComplexId && <QuickPriceRuleEquivalent id={`${editorId}-equivalent`} equivalent={equivalent}
        ruleMode={selectedResolution.resolvedRule?.mode}
        margin={getQuickRuleMargin(selectedResolution.resolvedRule, referenceCost)} basis={basis} />}
      {validationError && (
        <p id={`${editorId}-error`} className="mt-1 text-[11px] font-medium leading-tight text-red-600">
          {validationError}
        </p>
      )}
    </div>
  );
};

export default InlineQuickPriceRuleInput;
